import type { PersonalInfo } from '@/features/tax-profile/individual-profiles/PersonalInfo'
import { action, observable } from 'mobx'
import { required, validationContext } from 'validx'
import {
  mustBeInThePast,
  mustBeValidPersonalTaxpayerIdentificationNumber,
  mustBeValidTaxFormInputValue,
  mustHaveMaxLength,
} from '@/utils/validx-util'

/**
 * View model for editing personal information.
 */
export class PersonalInfoEditorViewModel {
  @observable accessor firstName = ''
  @observable accessor middleInitial = ''
  @observable accessor lastName = ''
  @observable accessor dateOfBirth: Date | null = null
  @observable accessor personalTaxpayerIdentificationNumber = ''

  readonly validation = validationContext(this, {
    firstName: [
      required('First name is required'),
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(100, 'First name must not exceed 100 characters'),
    ],
    middleInitial: [
      mustHaveMaxLength(1, 'Middle initial must be a single character'),
      mustBeValidTaxFormInputValue(),
    ],
    lastName: [
      required('Last name is required'),
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(100, 'Last name must not exceed 100 characters'),
    ],
    dateOfBirth: [
      required('Date of birth is required'),
      mustBeInThePast('Date of birth must be in the past'),
    ],
    personalTaxpayerIdentificationNumber: [
      required('SSN/ITIN is required'),
      mustBeValidPersonalTaxpayerIdentificationNumber(),
    ],
  })

  /**
   * Resets the state of the view model to that of the given
   * info, or defaults if none is specified.
   *
   * @param info
   */
  @action.bound
  reset(info?: PersonalInfo) {
    this.firstName = info?.firstName ?? ''
    this.middleInitial = info?.middleInitial ?? ''
    this.lastName = info?.lastName ?? ''
    this.dateOfBirth = info?.dateOfBirth ?? null
    this.personalTaxpayerIdentificationNumber =
      info?.personalTaxpayerIdentificationNumber ?? ''

    this.validation.reset()
  }

  /**
   * Maps the state of the VM to {@link PersonalInfo}.
   */
  toPersonalInfo(): PersonalInfo {
    return {
      firstName: this.firstName,
      middleInitial: this.middleInitial,
      lastName: this.lastName,
      personalTaxpayerIdentificationNumber:
        this.personalTaxpayerIdentificationNumber,
      dateOfBirth: this.dateOfBirth,
    }
  }
}
